/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
/**
 * 检测目标元素是否进入可视区域的 Hook
 * @param options - Intersection Observer 配置项
 * @param options.root - 根元素（默认：视口）
 * @param options.rootMargin - 根元素边距（扩展/缩小判定范围，如 "0px"、"-100px"）
 * @param options.threshold - 触发阈值（0~1，如 0.5 表示元素 50% 进入时触发）
 * @returns { ref: React.RefObject, isVisible: boolean } - 目标元素 ref + 是否可见
 */
declare type IntersectionObserverInit = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
};

const useIntersectionObserver = (
  options: IntersectionObserverInit = {
    root: null, // 默认根元素是视口
    rootMargin: '0px', // 默认无扩展
    threshold: 0.1, // 默认元素 10% 进入时判定为可见
  }
) => {
  // 存储目标元素的 ref
  const targetRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  // 存储元素是否可见的状态
  const [isVisible, setIsVisible] = useState<boolean>(false);

  // 处理 Intersection Observer 回调（元素可见性变化时触发）
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      // entries 是所有被观察元素的交集信息数组
      entries.forEach((entry) => {
        // entry.isIntersecting 为 true 表示元素进入可视区域
        setIsVisible((prevVisible) =>
          prevVisible ? prevVisible : entry.isIntersecting
        );
      });
    },
    []
  );

  // 初始化/销毁 Intersection Observer
  useEffect(() => {
    // 1. 检查浏览器是否支持 Intersection Observer（现代浏览器均支持）
    if (!window.IntersectionObserver) {
      console.warn('当前浏览器不支持 Intersection Observer，建议降级处理');
      return;
    }

    // 2. 创建 Intersection Observer 实例
    const observer = new window.IntersectionObserver(
      handleIntersection,
      options
    );

    // 3. 获取目标元素，开始观察（需确保元素已挂载）
    const target = targetRef.current;
    if (target) {
      observer.observe(target);
    }

    // 4. 组件卸载时，停止观察（避免内存泄漏）
    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [handleIntersection, options]);

  return { targetRef, isVisible };
};

export default useIntersectionObserver;
